import { faker } from '@faker-js/faker';
import { useQuery } from '@tanstack/react-query';
import { http, HttpResponse } from 'msw';
import { useMemo } from 'react';

import { env } from '@/config/env';
import { STABLE_MODULE_IDS } from '@/features/assessments/api/modules-list';
import { axios } from '@/lib/axios';
import { networkDelay } from '@/test/mocks/utils';

import {
  GetTeacherRemarksParams,
  TeacherRemark,
  TeacherRemarksResponse,
} from './types';

export const getTeacherRemarks = ({
  batch_id,
  profile_id,
  generic_id,
}: GetTeacherRemarksParams): Promise<TeacherRemarksResponse> => {
  return axios.get('/user/v1/remarks', {
    params: {
      batch_id,
      profile_id,
      generic_id,
    },
  });
};

// Query key factory
export const teacherRemarksKeys = {
  all: ['teacher-remarks'] as const,
  lists: () => [...teacherRemarksKeys.all, 'list'] as const,
  list: (params: GetTeacherRemarksParams) =>
    [...teacherRemarksKeys.lists(), params] as const,
};

// TanStack Query hook
const useTeacherRemarksQuery = (params: GetTeacherRemarksParams) => {
  return useQuery({
    queryKey: teacherRemarksKeys.list(params),
    queryFn: () => getTeacherRemarks(params),
    enabled: !!params.batch_id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Exported hook with structured return
export const useTeacherRemarks = (params: GetTeacherRemarksParams) => {
  const query = useTeacherRemarksQuery(params);

  const filteredRemarks = useMemo(() => {
    return (
      query.data?.remarks?.filter(
        r => params.profile_id && r.lead_id === params.profile_id
      ) ?? []
    );
  }, [query.data?.remarks, params.profile_id]);

  return {
    allRemarks: filteredRemarks,
    count: filteredRemarks.length ?? 0,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
};

// Mock Data Generators
const remarkTemplates = [
  'Excellent **progress!** You have shown great improvement in understanding the core concepts. Keep up the *fantastic* work!',
  '**Great work this module!** Here are some areas to focus on:\n- Practice more multiplication tables\n- Work on word problems\n- Review fraction concepts',
  'Your performance has been **outstanding**! I noticed:\n1. Strong understanding of algebra basics\n2. Excellent problem-solving approach\n3. Consistent improvement in speed',
  'Continue to practice regularly and focus on the areas where you struggled. *Remember*, practice makes perfect!',
  '**Well done!** Your dedication shows in your results. Keep working on:\n- Mental math calculations\n- *Understanding* the logic behind formulas\n- Applying concepts to real-world problems',
  'Your *dedication* is **commendable!** Keep up the great work and challenge yourself with harder problems.',
  "I'm impressed with your progress! Here's what stood out:\n1. **Improved accuracy** in calculations\n2. Better time management\n3. *Creative* problem-solving approaches",
  '**Fantastic effort!** Focus on these key areas:\n- Reviewing basic concepts regularly\n- Practicing under timed conditions\n- *Understanding* rather than memorizing',
  'Your **problem-solving skills** are improving *significantly*. Keep challenging yourself with more difficult problems!',
  'Good work overall! Areas for improvement:\n- Double-check your calculations\n- Read questions more *carefully*\n- Show your working clearly',
  '**Keep up the momentum!** Your consistent practice is paying off. *Stay curious* and keep exploring new concepts.',
  '**Excellent performance!** Your strengths:\n1. Strong conceptual understanding\n2. *Quick* mental calculations\n3. Neat presentation of solutions',
  'Your progress this module has been **remarkable**! Continue to:\n- Build on your strong foundation\n- *Challenge yourself* with advanced problems\n- Help classmates when possible',
  '**Outstanding work!** You have mastered the core concepts. Now focus on *applying* them to complex scenarios.',
  '**Great improvement!** Keep working on:\n- Time management during assessments\n- *Avoiding* silly mistakes\n- Building confidence in your abilities',
];

const generateTeacherRemark = (
  batchId: string,
  leadId: string,
  overrides: Partial<TeacherRemark> = {}
): TeacherRemark => {
  // Generate timestamp in seconds (not milliseconds) to match API behavior
  const createdOn = Math.floor(
    faker.date.recent({ days: 30 }).getTime() / 1000
  ).toString();
  const remarkId = faker.string.uuid();

  return {
    generic_id: null,
    remark_id: remarkId,
    created_by: faker.internet.email(),
    search_key: `LEAD_ID#${leadId}#CREATED_ON#${createdOn}#REMARK_ID#${remarkId}`,
    teacher_id:
      faker.helpers.maybe(() => faker.string.uuid(), {
        probability: 0.3,
      }) ?? null,
    batch_id: batchId,
    lead_id: leadId,
    is_module: faker.datatype.boolean(),
    remark_text: faker.helpers.arrayElement(remarkTemplates),
    created_on: createdOn,
    secondary_search_key: `GENERIC_ID#None#CREATED_ON#${createdOn}#REMARK_ID#${remarkId}`,
    ...overrides,
  };
};

const generateTeacherRemarks = (
  count: number,
  batchId: string,
  leadId: string,
  overrides: Partial<TeacherRemark> = {}
): TeacherRemark[] =>
  Array.from({ length: count }, () =>
    generateTeacherRemark(batchId, leadId, overrides)
  );

// MSW Mock Handler
export const getTeacherRemarksMockHandler = http.get(
  `${env.API_URL}/tms/v1/student-remarks`,
  async ({ request }): Promise<HttpResponse<TeacherRemarksResponse>> => {
    await networkDelay();

    const url = new URL(request.url);
    const batchId = url.searchParams.get('batch_id');
    const leadId = url.searchParams.get('lead_id') ?? faker.string.uuid();

    // Simulate error for missing required parameter
    if (!batchId) {
      return new HttpResponse(null, {
        status: 400,
        statusText: 'Missing required parameter: batch_id',
      });
    }

    // Generate remarks for each module with random count (0-2) and their respective generic_id
    const remarks: TeacherRemark[] = [
      STABLE_MODULE_IDS.ONGOING,
      STABLE_MODULE_IDS.COMPLETED,
    ].flatMap(moduleId => {
      const remarkCount = faker.number.int({ min: 1, max: 2 });
      return generateTeacherRemarks(remarkCount, batchId, leadId, {
        is_module: true,
        generic_id: moduleId,
      });
    });

    const mockResponse: TeacherRemarksResponse = {
      remarks,
      count: remarks.length,
    };

    return HttpResponse.json(mockResponse);
  }
);
